import React, {useEffect, useState} from "react";
import {useTranslation} from "next-i18next";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {
  selectIsSubmitting,
  selectRemainingQuestions,
  selectSurveyAnswers,
  setDietaryPreference
} from "../../redux/survey/macroSurveySlice";
import {DietaryPreference} from "../../models/fitnessProfile";
import {
  BentoBoxColorIcon,
  CarrotColorIcon,
  FishColorIcon,
  GlutenFreeColorIcon,
  PaleoColorIcon,
  VeganBowlColorIcon
} from "../icons";
import {HeroHeading2} from "../text";
import {MacroSurveyOptionItemView} from "./MacroSurveyOptionItemView";
import {Button} from "../buttons/Button";
import {LocalizationInfo} from "react-i18next";
import {EventLogger} from "../../utils/eventLogger";
import {AnalyticsEvent} from "../../utils/eventProperties";

export const MacroSurveyDietaryPreferenceQuestionView: React.FC<any> = ({  }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const answers = useAppSelector(selectSurveyAnswers)
  const remainingQuestions = useAppSelector(selectRemainingQuestions)
  const isSubmitting = useAppSelector(selectIsSubmitting)

  const getTitle = (dietaryPreference: DietaryPreference): LocalizationInfo => {
    switch (dietaryPreference) {
      case DietaryPreference.lowCarb:
        return { key: 'macro_survey:macro_survey_title_low_carb' }
      case DietaryPreference.lowFat:
        return { key: 'macro_survey:macro_survey_title_low_fat' }
      case DietaryPreference.vegetarian:
        return { key: 'macro_survey:macro_survey_title_vegetarian' }
      case DietaryPreference.vegan:
        return { key: 'macro_survey:macro_survey_title_vegan' }
      case DietaryPreference.everything:
        return { key: 'macro_survey:macro_survey_title_everything' }
      case DietaryPreference.paleo:
        return { key: 'macro_survey:macro_survey_title_paleo' }
    }
  }

  const getIcon = (dietaryPreference: DietaryPreference): React.ReactNode => {
    switch (dietaryPreference) {
      case DietaryPreference.lowCarb:
        return <GlutenFreeColorIcon />
      case DietaryPreference.lowFat:
        return <FishColorIcon />
      case DietaryPreference.vegetarian:
        return <CarrotColorIcon />
      case DietaryPreference.vegan:
        return <VeganBowlColorIcon />
      case DietaryPreference.everything:
        return <BentoBoxColorIcon />
      case DietaryPreference.paleo:
        return <PaleoColorIcon />
    }
  }

  const options: DietaryPreference[] = [
    DietaryPreference.lowFat,
    DietaryPreference.lowCarb,
    DietaryPreference.vegan,
    DietaryPreference.vegetarian,
    DietaryPreference.everything,
  ]
  const [dietaryPreferenceAnswer, setDietaryPreferenceAnswer] = useState<DietaryPreference | undefined>(answers.dietaryPreference)
  const [isValid, setIsValid] = useState<boolean>(false)

  useEffect(() => {
    EventLogger.logEvent(new AnalyticsEvent({ eventName: 'Screen Visited', screenName: 'Nutrition Survey' }, { 'Survey Question': 'Dietary Preference' }))
  }, [])

  useEffect(() => {
    if (dietaryPreferenceAnswer) {
      setIsValid(true)
    } else {
      setIsValid(false)
    }
  }, [dietaryPreferenceAnswer])

  return <div className="flex flex-col space-y-8 lg:space-y-10 items-center w-full">
    <HeroHeading2 className="text-center mx-8">{t('macro_survey:macro_survey_header_title_dietary_preference')}</HeroHeading2>

    <div className="w-full">
      {options.map(option => {
        return <div key={option.toString()} className="border-b border-fg-gray-surface last:border-b-0">
          <MacroSurveyOptionItemView
            item={{
              title: getTitle(option),
              id: option.toString(),
              isSelected: dietaryPreferenceAnswer === option
            }}
            icon={getIcon(option)}
            isLoading={false}
            onItemSelected={() => {
              setDietaryPreferenceAnswer(option)
            }}
          />
        </div>
      })}
    </div>

    <div className="w-full px-5 md:justify-center md:flex">
      <Button
        isDisabled={!isValid}
        isLoading={isSubmitting}
        size="lg"
        isFullWidth={true}
        onClick={() => {
          dietaryPreferenceAnswer && dispatch(setDietaryPreference(dietaryPreferenceAnswer))
        }}
      >
        {(remainingQuestions.length - 1) ? t('common:common_title_continue') : t('common:common_title_submit')}
      </Button>
    </div>
  </div>
}